import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SymbolInfoColumns } from 'symbols/symbolInfo/columns';

export interface ValidateResult {
    mappings: Array<{ attributeValid: boolean; indexValid: boolean }>;
}

export const initialValidateResult: ValidateResult = {
    mappings: [],
};

export interface IndexMappingEditorRow {
    index: string;
    attribute?: SymbolInfoColumns;
    value: string;
}

export interface IndexMappingsEditorPanelState {
    editingMappings?: IndexMappingEditorRow[];
    validationResult: ValidateResult;
    isDirty: boolean;
    saveAttempted: boolean;
}

export const initialIndexMappingsEditorPanelState: IndexMappingsEditorPanelState =
    {
        validationResult: initialValidateResult,
        isDirty: false,
        saveAttempted: false,
    };

const indexMappingsEditorPanelSlice = createSlice({
    name: 'indexMappingsEditorPanel',
    initialState: initialIndexMappingsEditorPanelState,
    reducers: {
        setEditingMappings: (
            state,
            action: PayloadAction<{
                mappings: IndexMappingEditorRow[];
                suppressSetDirty: boolean;
            }>
        ) => {
            state.editingMappings = action.payload.mappings;
            if (!action.payload.suppressSetDirty) {
                state.isDirty = true;
            }
        },
        setValidationResult: (state, action: PayloadAction<ValidateResult>) => {
            state.validationResult = action.payload;
        },
        add: (state) => {
            state.editingMappings!.push({
                value: '',
                index: '',
            });
            state.validationResult.mappings.push({
                attributeValid: false,
                indexValid: false,
            });
            state.isDirty = true;
        },
        remove: (state, action: PayloadAction<{ ordinal: number }>) => {
            state.editingMappings!.splice(action.payload.ordinal, 1);
            state.isDirty = true;
        },
        update: (
            state,
            action: PayloadAction<{
                ordinal: number;
                indexMapping: IndexMappingEditorRow;
            }>
        ) => {
            const { indexMapping, ordinal } = action.payload;
            state.isDirty = true;
            state.editingMappings![ordinal] = indexMapping;
        },
        move: (
            state,
            action: PayloadAction<{ ordinal: number; direction: 'up' | 'down' }>
        ) => {
            const { direction, ordinal } = action.payload;
            if (direction === 'up' && ordinal === 0) {
                return;
            }
            if (
                direction === 'down' &&
                ordinal === state.editingMappings!.length - 1
            ) {
                return;
            }
            const firstIndex = direction === 'up' ? ordinal - 1 : ordinal;
            const first = state.editingMappings![firstIndex];
            const second = state.editingMappings![firstIndex + 1];
            state.editingMappings![firstIndex] = second;
            state.editingMappings![firstIndex + 1] = first;
            state.isDirty = true;
        },

        setMappingIndexValid: (
            state,
            action: PayloadAction<{ ordinal: number; isValid: boolean }>
        ) => {
            const { ordinal, isValid } = action.payload;
            if (state.validationResult.mappings[ordinal]) {
                state.validationResult.mappings[ordinal].indexValid = isValid;
            }
        },

        setMappingAttributeValid: (
            state,
            action: PayloadAction<{ ordinal: number; isValid: boolean }>
        ) => {
            const { ordinal, isValid } = action.payload;
            if (state.validationResult.mappings[ordinal]) {
                state.validationResult.mappings[ordinal].attributeValid =
                    isValid;
            }
        },
        reset: (state) => {
            state.isDirty = false;
            state.saveAttempted = false;
        },
        saveAttempted: (state) => {
            state.saveAttempted = true;
        },
    },
});

export const actions = indexMappingsEditorPanelSlice.actions;

export default indexMappingsEditorPanelSlice.reducer;
